import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { socialAccountsApi, type ConnectedAccount, type Platform } from '../api/socialAccounts'
import { ApiError } from '../api/client'

type Banner =
  | { kind: 'success'; text: string }
  | { kind: 'error'; text: string }

interface PlatformCard {
  platform: Platform
  label: string
  description: string
  connect: () => Promise<{ authorizationUrl: string }>
  disconnect: (id: string) => Promise<void>
}

const platforms: PlatformCard[] = [
  {
    platform: 'YouTube',
    label: 'YouTube',
    description: 'Publicera videor, läs kommentarer och statistik.',
    connect: () => socialAccountsApi.connectGoogle(),
    disconnect: (id) => socialAccountsApi.disconnectGoogle(id),
  },
  {
    platform: 'Facebook',
    label: 'Facebook + Instagram',
    description: 'En anslutning ger båda: publicering, kommentarer och insights.',
    connect: () => socialAccountsApi.connectMeta(),
    disconnect: (id) => socialAccountsApi.disconnectMeta(id),
  },
  {
    platform: 'TikTok',
    label: 'TikTok',
    description:
      'Publicering till din inkorg för slutförande i appen. Ogranskad app kan '
      + 'bara posta privat (SELF_ONLY) tills TikToks audit är klar.',
    connect: () => socialAccountsApi.connectTikTok(),
    disconnect: (id) => socialAccountsApi.disconnectTikTok(id),
  },
]

function statusBadge(status: ConnectedAccount['status']) {
  switch (status) {
    case 'Connected': return { text: 'Ansluten', kind: 'ok' as const }
    case 'RequiresReauth': return { text: 'Behöver återanslutas', kind: 'warn' as const }
    case 'TokenExpired': return { text: 'Token utgången', kind: 'warn' as const }
    case 'Revoked': return { text: 'Behörighet indragen', kind: 'error' as const }
    case 'Disconnected': return { text: 'Frånkopplad', kind: 'muted' as const }
  }
}

export function SocialAccountsPage() {
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([])
  const [banner, setBanner] = useState<Banner | null>(null)
  const [busyPlatform, setBusyPlatform] = useState<Platform | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  const load = useCallback(async () => {
    try {
      const rows = await socialAccountsApi.list()
      setAccounts(rows)
    } catch {
      setBanner({ kind: 'error', text: 'Kunde inte hämta anslutna konton.' })
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // Callback-URL kommer tillbaka med ?connected=youtube|meta eller ?error=...
  useEffect(() => {
    const connected = searchParams.get('connected')
    const error = searchParams.get('error')
    if (connected) {
      const label = connected === 'youtube' ? 'YouTube'
        : connected === 'meta' ? 'Facebook + Instagram'
        : connected === 'tiktok' ? 'TikTok'
        : connected
      setBanner({ kind: 'success', text: `Kontot för ${label} är anslutet.` })
      searchParams.delete('connected')
      searchParams.delete('channel')
      searchParams.delete('account')
      setSearchParams(searchParams, { replace: true })
      void load()
    } else if (error) {
      const map: Record<string, string> = {
        access_denied: 'Anslutningen avbröts.',
        invalid_state: 'Säkerhetstoken matchade inte. Försök igen.',
        expired_state: 'Anslutningen tog för lång tid. Försök igen.',
        exchange_failed: 'Plattformen svarade inte som förväntat. Försök igen.',
        no_youtube_channel:
          'Google-kontot du valde har ingen YouTube-kanal. Gå till youtube.com, ' +
          'skapa en kanal och försök ansluta igen — eller välj ett annat Google-konto.',
        no_facebook_page:
          'Ingen Facebook-sida beviljades. Klicka "Anslut" igen och välj ' +
          '"Edit settings" i Metas dialog (inte "Reconnect") — bocka sedan i din ' +
          'Facebook-sida och ge både publicerings- och moderatorbehörighet.',
        meta_page_not_assigned:
          'Din Facebook-sida hittades i företagsportföljen, men du saknar behörighet ' +
          'på själva sidan. Gå till Meta Business Settings → Konton → Sidor → välj sidan ' +
          '→ Personer, och ge dig själv "Full control". Kör sedan Anslut igen.',
        meta_permission_denied:
          'Meta nekade ett anrop. Troligen saknas ett scope i Business Login-' +
          'konfigurationen (t.ex. instagram_basic för att läsa det länkade IG-kontot).',
        meta_consent_unverifiable:
          'Vi kunde inte fastställa exakt vilka sidor du beviljade, och lagrar därför ' +
          'inget. Klicka "Anslut" igen, välj "Edit settings" i Metas dialog, och kryssa ' +
          'i sidorna under "Opt in to current Pages only".',
      }
      setBanner({ kind: 'error', text: map[error] ?? `Anslutningen misslyckades (${error}).` })
      searchParams.delete('error')
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  async function onConnect(card: PlatformCard) {
    setBanner(null)
    setBusyPlatform(card.platform)
    try {
      const { authorizationUrl } = await card.connect()
      // Full navigation — popup blockeras av tredje-parts-cookie-policy.
      window.location.assign(authorizationUrl)
    } catch (e) {
      const message = e instanceof ApiError
        ? `Kunde inte starta anslutningen (${e.status}).`
        : 'Kunde inte starta anslutningen.'
      setBanner({ kind: 'error', text: message })
      setBusyPlatform(null)
    }
  }

  async function onDisconnect(card: PlatformCard, account: ConnectedAccount) {
    if (!window.confirm(`Koppla bort ${account.accountName} (${card.label})?`)) return
    setBusyPlatform(card.platform)
    try {
      await card.disconnect(account.id)
      setBanner({ kind: 'success', text: `${account.accountName} bortkopplat.` })
      await load()
    } catch {
      setBanner({ kind: 'error', text: 'Frånkoppling misslyckades.' })
    } finally {
      setBusyPlatform(null)
    }
  }

  return (
    <main className="page">
      <h1>Anslutna konton</h1>
      <p className="muted">
        Koppla dina plattformskonton. Access-tokens lagras krypterade och
        förnyas automatiskt.
      </p>

      {banner && (
        <div role="status" className={`banner banner-${banner.kind}`}>
          {banner.text}
        </div>
      )}

      <ul className="platform-list">
        {platforms.map((card) => {
          const rows = accounts.filter((a) => matches(a.platform, card.platform))
          return (
            <li key={card.platform} className="platform-card">
              <div className="platform-head">
                <h2>{card.label}</h2>
                <button
                  type="button"
                  className="primary"
                  onClick={() => onConnect(card)}
                  disabled={busyPlatform === card.platform}
                >
                  {rows.length === 0 ? 'Anslut' : 'Anslut ytterligare'}
                </button>
              </div>
              <p className="muted small">{card.description}</p>

              {rows.length === 0 ? (
                <p className="muted small no-accounts">Inga konton anslutna än.</p>
              ) : (
                <ul className="account-list">
                  {rows.map((account) => {
                    const badge = statusBadge(account.status)
                    return (
                      <li key={account.id} className="account-row">
                        <div>
                          <div className="account-name">{account.accountName}</div>
                          <div className="muted small">
                            {account.platform} · {account.externalAccountId}
                          </div>
                        </div>
                        <span className={`badge badge-${badge.kind}`}>{badge.text}</span>
                        <button
                          type="button"
                          className="link"
                          onClick={() => onDisconnect(card, account)}
                          disabled={busyPlatform === card.platform}
                        >
                          Koppla bort
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </li>
          )
        })}
      </ul>
    </main>
  )
}

// Meta ger både Facebook- och Instagram-rader från en anslutning.
function matches(rowPlatform: Platform, cardPlatform: Platform): boolean {
  if (cardPlatform === 'Facebook') return rowPlatform === 'Facebook' || rowPlatform === 'Instagram'
  return rowPlatform === cardPlatform
}
